import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Collapse,
  Form,
  Input,
  Popconfirm,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
  type FixedRupResource,
  type KrutRupResource,
  type TingantaFixedRupRow,
  type TingantaKrutRow,
  buildFixedRupPatch,
  buildKrutPatch,
  type KrutValueSegment,
  krutPairsFromSegmentForm,
  krutRowToSegmentPairs,
  pickEmbeddedFixedRup,
  pickEmbeddedKrutRow,
  rowToFormValues,
  tingantaRelationEmbedKeys,
  tingantaKrut,
  tingantaNichKrut,
  tingantaNichKarmani,
  tingantaNichKartari,
  tingantaRupKarmani,
  tingantaRupKartari,
  tingantaSanKrut,
  tingantaSanKarmani,
  tingantaSanKartari,
  tingantaYangKrut,
  tingantaYangKarmani,
  tingantaYangKartari,
  tingantaYanglukKrut,
  tingantaYanglukKarmani,
  tingantaYanglukKartari,
} from '../api/tingantaRupApi';
import type { Tinganta } from '../api/tingantaApi';
import {
  TingantaNichKarmaniFormItems,
  TingantaNichKartariAtmanepadFormItems,
  TingantaNichKartariParasmaiFormItems,
  TingantaRupKarmaniFormItems,
  TingantaRupKartariAtmanepadFormItems,
  TingantaRupKartariParasmaiFormItems,
  TingantaSanKarmaniFormItems,
  TingantaSanKartariAtmanepadFormItems,
  TingantaSanKartariParasmaiFormItems,
  TingantaYangKarmaniFormItems,
  TingantaYangKartariFormItems,
  TingantaYanglukKarmaniFormItems,
  TingantaYanglukKartariParasmaiFormItems,
} from './TingantaRupKartariParasmaiGrids';
import { TingantaKrutPratyayaFormList } from './TingantaKrutPratyayaForm';

const RUP_KARTARI_CODE = 'tinganta-rup-kartari';
const RUP_KARMANI_CODE = 'tinganta-rup-karmani';
const SAN_KARTARI_CODE = 'tinganta-san-kartari';
const SAN_KARMANI_CODE = 'tinganta-san-karmani';
const NICH_KARTARI_CODE = 'tinganta-nich-kartari';
const NICH_KARMANI_CODE = 'tinganta-nich-karmani';
const YANG_KARTARI_CODE = 'tinganta-yang-kartari';
const YANG_KARMANI_CODE = 'tinganta-yang-karmani';
const YANGLUK_KARTARI_CODE = 'tinganta-yangluk-kartari';
const YANGLUK_KARMANI_CODE = 'tinganta-yangluk-karmani';

const emptyKrutFormPair = (): { key: string; segments: KrutValueSegment[] } => ({
  key: '',
  segments: [{ base: '', pu: '', st: '', na: '' }],
});

function errMsg(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return (
    ax?.response?.data?.message || ax?.message || 'Request failed'
  );
}

type FixedConfig = {
  key: string;
  title: string;
  code: string;
  resource: FixedRupResource;
};

const FIXED_RUP_CONFIG: FixedConfig[] = [
  { key: 'rup-k', title: 'Rūpa kartari', code: 'tinganta-rup-kartari', resource: tingantaRupKartari },
  { key: 'rup-m', title: 'Rūpa karmani', code: 'tinganta-rup-karmani', resource: tingantaRupKarmani },
  { key: 'san-k', title: 'San kartari', code: 'tinganta-san-kartari', resource: tingantaSanKartari },
  { key: 'san-m', title: 'San karmani', code: 'tinganta-san-karmani', resource: tingantaSanKarmani },
  { key: 'nich-k', title: 'Ṇij kartari', code: 'tinganta-nich-kartari', resource: tingantaNichKartari },
  { key: 'nich-m', title: 'Ṇij karmani', code: 'tinganta-nich-karmani', resource: tingantaNichKarmani },
  { key: 'yang-k', title: 'Yaṅ kartari', code: 'tinganta-yang-kartari', resource: tingantaYangKartari },
  { key: 'yang-m', title: 'Yaṅ karmani', code: 'tinganta-yang-karmani', resource: tingantaYangKarmani },
  { key: 'yangluk-k', title: 'Yaṅ-luk kartari', code: 'tinganta-yangluk-kartari', resource: tingantaYanglukKartari },
  { key: 'yangluk-m', title: 'Yaṅ-luk karmani', code: 'tinganta-yangluk-karmani', resource: tingantaYanglukKarmani },
];

type KrutConfig = { key: string; title: string; code: string; resource: KrutRupResource };

const KRUT_CONFIG: KrutConfig[] = [
  { key: 'krut', title: 'Kṛt', code: 'tinganta-krut', resource: tingantaKrut },
  { key: 'san-krut', title: 'San kṛt', code: 'tinganta-san-krut', resource: tingantaSanKrut },
  { key: 'nich-krut', title: 'Ṇij kṛt', code: 'tinganta-nich-krut', resource: tingantaNichKrut },
  { key: 'yang-krut', title: 'Yaṅ kṛt', code: 'tinganta-yang-krut', resource: tingantaYangKrut },
  { key: 'yangluk-krut', title: 'Yaṅ-luk kṛt', code: 'tinganta-yangluk-krut', resource: tingantaYanglukKrut },
];

function TingantaFixedRupPanelBody({
  tingantaId,
  detail,
  active,
  resource,
  code,
  onReload,
}: {
  tingantaId: number;
  detail: Tinganta | null;
  active: boolean;
  resource: FixedRupResource;
  code: string;
  onReload: () => Promise<void>;
}) {
  const [row, setRow] = useState<TingantaFixedRupRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<Record<string, string>>();

  const embedKeys = useMemo(() => tingantaRelationEmbedKeys(resource.basePath), [resource.basePath]);
  const detailRec = detail ? (detail as unknown as Record<string, unknown>) : null;

  const syncFromApi = useCallback(async () => {
    if (!tingantaId) return;
    const embedded = pickEmbeddedFixedRup(detailRec, embedKeys, tingantaId);
    if (embedded) {
      setRow(embedded);
      form.setFieldsValue(rowToFormValues(embedded, resource.scalarKeys));
      return;
    }
    setLoading(true);
    try {
      const rows = await resource.list(tingantaId);
      const r = rows[0] ?? null;
      setRow(r);
      form.setFieldsValue(rowToFormValues(r, resource.scalarKeys));
    } catch (err: unknown) {
      message.error(errMsg(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [detailRec, embedKeys, form, resource, tingantaId]);

  useEffect(() => {
    if (!active || !tingantaId) return;
    void syncFromApi();
  }, [active, tingantaId, syncFromApi]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (row) {
        const patch = buildFixedRupPatch(row, values, resource.scalarKeys);
        if (Object.keys(patch).length === 0) {
          message.info('No changes');
          return;
        }
        await resource.update(row.id, patch);
        message.success('Updated');
      } else {
        await resource.create({ tingantaId, ...values });
        message.success('Created');
      }
      await onReload();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await resource.remove(row.id);
      message.success('Deleted');
      setRow(null);
      form.setFieldsValue(rowToFormValues(null, resource.scalarKeys));
      await onReload();
    } catch (err: unknown) {
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;

  return (
    <Spin spinning={loading}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        <code>{code}</code> — at most one row per tiṅganta. Optional string fields.
      </Typography.Paragraph>
      {row ? (
        <Typography.Text type="secondary">
          Row id <strong>{row.id}</strong>
        </Typography.Text>
      ) : (
        <Typography.Paragraph type="secondary">No row yet — fill fields and save to create.</Typography.Paragraph>
      )}
      <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
        {code === RUP_KARTARI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Parasmaipada: each table is one lakāra; cells are the nine forms in order (प्रथम–मध्यम–उत्तम ×
              एकवचन–द्विवचन–बहुवचन), stored joined by semicolons in the API.
            </Typography.Paragraph>
            <TingantaRupKartariParasmaiFormItems />
            <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>
              Ātmanepad (कर्तरि)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              Same ३×३ grid and semicolon convention as parasmaipada.
            </Typography.Paragraph>
            <TingantaRupKartariAtmanepadFormItems />
          </>
        ) : code === RUP_KARMANI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              कर्मणि: ten lakārāḥ (ātmanepad-shaped fields). Same ३×३ grid and semicolon storage as rūpa kartari.
            </Typography.Paragraph>
            <TingantaRupKarmaniFormItems />
          </>
        ) : code === SAN_KARTARI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              सन्नन्ते कर्तरि — parasmaipada: same ३×३ layout and semicolon convention as rūpa kartari.
            </Typography.Paragraph>
            <TingantaSanKartariParasmaiFormItems />
            <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>
              Ātmanepad (सन्नन्ते कर्तरि)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              Same grid and semicolon convention as parasmaipada.
            </Typography.Paragraph>
            <TingantaSanKartariAtmanepadFormItems />
          </>
        ) : code === SAN_KARMANI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              सन्नन्ते कर्मणि — ten lakārāḥ. Same ३×३ grid and semicolon storage.
            </Typography.Paragraph>
            <TingantaSanKarmaniFormItems />
          </>
        ) : code === NICH_KARTARI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              णिजन्ते कर्तरि — parasmaipada: same ३×३ layout and semicolon convention.
            </Typography.Paragraph>
            <TingantaNichKartariParasmaiFormItems />
            <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>
              Ātmanepad (णिजन्ते कर्तरि)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              Same grid and semicolon convention as parasmaipada.
            </Typography.Paragraph>
            <TingantaNichKartariAtmanepadFormItems />
          </>
        ) : code === NICH_KARMANI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              णिजन्ते कर्मणि — ten lakārāḥ. Same ३×३ grid and semicolon storage.
            </Typography.Paragraph>
            <TingantaNichKarmaniFormItems />
          </>
        ) : code === YANG_KARTARI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              यङन्ते कर्तरि — ten lakārāḥ (same <code>alat</code>…<code>alrung</code> fields as API). Same ३×३
              grid and semicolon storage as rūpa kartari ātmanepad.
            </Typography.Paragraph>
            <TingantaYangKartariFormItems />
          </>
        ) : code === YANG_KARMANI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              यङन्ते कर्मणि — ten lakārāḥ. Same ३×३ grid and semicolon storage.
            </Typography.Paragraph>
            <TingantaYangKarmaniFormItems />
          </>
        ) : code === YANGLUK_KARTARI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              यङ्लुकि कर्तरि — parasmaipada only (<code>plat</code>…<code>plrung</code>). Same ३×३ grid and
              semicolon storage.
            </Typography.Paragraph>
            <TingantaYanglukKartariParasmaiFormItems />
          </>
        ) : code === YANGLUK_KARMANI_CODE ? (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              यङ्लुकि कर्मणि — ten lakārāḥ. Same ३×३ grid and semicolon storage.
            </Typography.Paragraph>
            <TingantaYanglukKarmaniFormItems />
          </>
        ) : (
          resource.scalarKeys.map((k) => (
            <Form.Item key={k} name={k} label={k}>
              <Input.TextArea rows={2} placeholder="Optional" />
            </Form.Item>
          ))
        )}
        <Space wrap>
          <Button type="primary" onClick={() => void onSave()} loading={saving}>
            {row ? 'Save changes' : 'Create'}
          </Button>
          {row ? (
            <Popconfirm title="Delete this row?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void onDelete()}>
              <Button danger icon={<DeleteOutlined />} loading={saving}>
                Delete
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      </Form>
    </Spin>
  );
}

function TingantaKrutPanelBody({
  tingantaId,
  detail,
  active,
  resource,
  code,
  onReload,
}: {
  tingantaId: number;
  detail: Tinganta | null;
  active: boolean;
  resource: KrutRupResource;
  code: string;
  onReload: () => Promise<void>;
}) {
  const [row, setRow] = useState<TingantaKrutRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<{ pairs: { key: string; segments: KrutValueSegment[] }[] }>();

  const embedKeys = useMemo(() => tingantaRelationEmbedKeys(resource.basePath), [resource.basePath]);
  const detailRec = detail ? (detail as unknown as Record<string, unknown>) : null;

  const syncFromApi = useCallback(async () => {
    if (!tingantaId) return;
    const embedded = pickEmbeddedKrutRow(detailRec, embedKeys, tingantaId);
    if (embedded) {
      setRow(embedded);
      const pairs = krutRowToSegmentPairs(embedded);
      form.setFieldsValue({
        pairs: pairs.length > 0 ? pairs : [emptyKrutFormPair()],
      });
      return;
    }
    setLoading(true);
    try {
      const rows = await resource.list(tingantaId);
      const r = rows[0] ?? null;
      setRow(r);
      const pairs = krutRowToSegmentPairs(r);
      form.setFieldsValue({
        pairs: pairs.length > 0 ? pairs : [emptyKrutFormPair()],
      });
    } catch (err: unknown) {
      message.error(errMsg(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [detailRec, embedKeys, form, resource, tingantaId]);

  useEffect(() => {
    if (!active || !tingantaId) return;
    void syncFromApi();
  }, [active, tingantaId, syncFromApi]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const pairs = krutPairsFromSegmentForm(values.pairs);
      if (pairs.length === 0 && !row) {
        message.warning('Add at least one key/value pair to create.');
        return;
      }
      setSaving(true);
      if (row) {
        const patch = buildKrutPatch(row, pairs);
        if (Object.keys(patch).length === 0) {
          message.info('No changes');
          return;
        }
        await resource.update(row.id, patch);
        message.success('Updated');
      } else {
        const body: Record<string, unknown> = { tingantaId };
        for (const p of pairs) {
          const v = p.value?.trim();
          if (v) body[p.key] = v;
        }
        await resource.create(body as { tingantaId: number } & Record<string, unknown>);
        message.success('Created');
      }
      await onReload();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await resource.remove(row.id);
      message.success('Deleted');
      setRow(null);
      form.setFieldsValue({ pairs: [emptyKrutFormPair()] });
      await onReload();
    } catch (err: unknown) {
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;

  return (
    <Spin spinning={loading}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        <code>{code}</code> — kṛt pratyayas: each value is stored as{' '}
        <code>base,प्रथम-पुं,स्त्री,नपुं</code> (optional empties). Multiple variants are separated by{' '}
        <code>;</code>.
      </Typography.Paragraph>
      {row ? (
        <Typography.Text type="secondary">
          Row id <strong>{row.id}</strong>
        </Typography.Text>
      ) : (
        <Typography.Paragraph type="secondary">No row yet.</Typography.Paragraph>
      )}
      <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
        <TingantaKrutPratyayaFormList />
        <Space wrap>
          <Button type="primary" onClick={() => void onSave()} loading={saving}>
            {row ? 'Save changes' : 'Create'}
          </Button>
          {row ? (
            <Popconfirm title="Delete this row?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void onDelete()}>
              <Button danger icon={<DeleteOutlined />} loading={saving}>
                Delete
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      </Form>
    </Spin>
  );
}

export type TingantaRupSectionsProps = {
  tingantaId: number;
  detail: Tinganta | null;
  onReload: () => Promise<void>;
};

export function TingantaRupSections({ tingantaId, detail, onReload }: TingantaRupSectionsProps) {
  const [openKeys, setOpenKeys] = useState<string | string[]>([]);

  const fixedItems = useMemo(
    () =>
      FIXED_RUP_CONFIG.map((cfg) => ({
        key: cfg.key,
        label: (
          <span>
            {cfg.title} <Typography.Text type="secondary">({cfg.code})</Typography.Text>
          </span>
        ),
        children: (
          <TingantaFixedRupPanelBody
            tingantaId={tingantaId}
            detail={detail}
            active={Array.isArray(openKeys) ? openKeys.includes(cfg.key) : openKeys === cfg.key}
            resource={cfg.resource}
            code={cfg.code}
            onReload={onReload}
          />
        ),
      })),
    [detail, tingantaId, onReload, openKeys]
  );

  const krutItems = useMemo(
    () =>
      KRUT_CONFIG.map((cfg) => ({
        key: cfg.key,
        label: (
          <span>
            {cfg.title} <Typography.Text type="secondary">({cfg.code})</Typography.Text>
          </span>
        ),
        children: (
          <TingantaKrutPanelBody
            tingantaId={tingantaId}
            detail={detail}
            active={Array.isArray(openKeys) ? openKeys.includes(cfg.key) : openKeys === cfg.key}
            resource={cfg.resource}
            code={cfg.code}
            onReload={onReload}
          />
        ),
      })),
    [detail, tingantaId, onReload, openKeys]
  );

  return (
    <div style={{ marginTop: 16 }}>
      <Typography.Title level={5} style={{ marginBottom: 8 }}>
        Tiṅganta rūpa &amp; kṛt
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Open a section to edit. Values are read from <code>GET /tinganta/:id</code> when nested there,
        otherwise from the list endpoints (optional <code>?tingantaId=</code>).
      </Typography.Paragraph>
      <Collapse
        accordion={false}
        activeKey={openKeys}
        onChange={(k) => setOpenKeys(k)}
        items={[...fixedItems, ...krutItems]}
      />
    </div>
  );
}
